import { CheckCircle, XCircle } from "phosphor-react";

/**
 * Password requirements configuration - matches backend validation
 * Backend is authoritative; this is UI-only feedback
 */
export const PASSWORD_REQUIREMENTS = {
  minLength: 12,
  minCharClasses: 3,
  charClasses: ["lowercase", "uppercase", "number", "symbol"],
};

/**
 * Check password strength for UI feedback (not authoritative - backend validates)
 * @param {string} password - Password to check
 * @returns {object} - Strength check results
 */
export function checkPasswordStrength(password) {
  const checks = {
    length: password.length >= PASSWORD_REQUIREMENTS.minLength,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    symbol: /[^a-zA-Z0-9]/.test(password),
  };
  const classCount = [checks.lowercase, checks.uppercase, checks.number, checks.symbol].filter(Boolean).length;
  return {
    ...checks,
    classCount,
    meetsRequirements: checks.length && classCount >= PASSWORD_REQUIREMENTS.minCharClasses,
  };
}

/**
 * Password requirements checklist component
 * Displays live feedback on password strength (UI only - backend is authoritative)
 * 
 * @param {object} props
 * @param {string} props.password - Current password value
 * @param {string} [props.className] - Additional CSS classes
 */
export function PasswordRequirements({ password, className = "" }) {
  if (!password) return null;
  
  const strength = checkPasswordStrength(password);
  
  return (
    <div className={`mt-2 space-y-1 text-xs ${className}`}>
      {/* Length requirement */}
      <div className={`flex items-center gap-1 ${strength.length ? 'text-green-600' : 'text-zinc-400'}`}>
        {strength.length ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} />}
        <span>At least 12 characters</span>
      </div>
      
      {/* Character class requirement */}
      <div className={`flex items-center gap-1 ${strength.classCount >= 3 ? 'text-green-600' : 'text-zinc-400'}`}>
        {strength.classCount >= 3 ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} />}
        <span>At least 3 of: lowercase, uppercase, number, symbol ({strength.classCount}/4)</span>
      </div>
    </div>
  );
}

export default PasswordRequirements;
